'use client';

/**
 * The row under the trip name: when you go, what you drive, how far a day.
 *
 *   ┌  + Add dates  ┬  🚐 Caravan ⌄  ┬  🕒 6h a day ⌄  ┐
 *
 * One segmented control: three equal cells in one 44px outline, the same on
 * every screen, each one tap target with an icon, a word and a chevron,
 * centred as one group. An empty date is an offer ("+ Add dates", a brand
 * plus, the words in ink), never a greyed-out value. The
 * why ("Sets road & fuel rules") is the tooltip, not a second line. The
 * date is a styled trigger over the native picker (showPicker), so it never
 * shows a raw "dd/mm/yyyy" box. The selects are real <select>s laid
 * invisibly over the drawn label, so keyboard and screen readers get the
 * native control.
 */
import React, { useRef } from 'react';
import { dateForTripDay } from '../../utils/tripPlanner';
import { shortDate } from './geo';
import { IconCalendar, IconCar, IconCaravan, IconChevronDown, IconClock, IconMotorhome, IconOffroad, IconPlus, IconX } from './icons';

export const VEHICLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'car', label: 'Car' },
  { value: '4wd', label: '4WD' },
  { value: 'caravan', label: 'Caravan' },
  { value: 'motorhome', label: 'Motorhome' },
];

/**
 * What the vehicle actually changes, in a few words. One phrase in both
 * the phone chip and the desktop control. Caravans and motorhomes route on
 * the small-truck profile (slower drive times) and get camps that fit a
 * big rig; cars and 4WDs route at car speeds.
 */
export function vehicleEffect(type: string): string {
  switch (type) {
    case 'caravan':
      return 'towing speeds';
    case 'motorhome':
    case 'campervan':
      return 'big-rig speeds';
    default:
      return 'car speeds';
  }
}

export function vehicleIcon(type: string, className = 'h-4 w-4') {
  switch (type) {
    case '4wd':
      return <IconOffroad className={className} />;
    case 'caravan':
      return <IconCaravan className={className} />;
    case 'motorhome':
    case 'campervan':
      return <IconMotorhome className={className} />;
    default:
      return <IconCar className={className} />;
  }
}

/** "Thu 12 Nov – Mon 16 Nov" (or just the start when there are no nights). */
export function dateRangeLabel(startDate: string | null | undefined, nights: number): string {
  if (!startDate) return '';
  const start = shortDate(startDate);
  if (!start) return '';
  if (nights <= 0) return start;
  const end = shortDate(dateForTripDay(startDate, nights));
  return end ? `${start} – ${end}` : start;
}

const VEHICLE_TITLE = 'Your vehicle sets the drive times and which camps we suggest';

// One cell of the segmented control: it fills its third whatever the words
// inside are. 44px: a comfortable tap. The outline belongs to the group.
// Icon, word and chevron travel together as one group, centred in the cell:
// the chevron always sits 4px after its word, in every cell.
const CONTROL =
  'relative flex h-11 min-h-0 w-full min-w-0 items-center justify-center gap-1.5 bg-white px-2 text-tp-control text-tp-ink first:rounded-l-[9px] last:rounded-r-[9px] ' +
  'transition-[background-color,box-shadow] duration-150 ease-tp-out hover:bg-tp-surface ' +
  'focus-within:z-[1] focus-within:shadow-[inset_0_0_0_2px_rgba(21,88,176,0.55)]';

/** "Thu 12 Nov" — the date cell's word (the full range is its tooltip). Pure. */
export function dateCellLabel(startDate: string | null | undefined): string {
  return startDate ? shortDate(startDate) || '' : '';
}

// Phones: a 36px pill, the value in ink, one line.
const CHIP =
  'relative flex h-9 min-h-0 flex-none items-center gap-1.5 rounded-full border border-solid border-tp-line-strong bg-white pl-3 text-[14px] font-semibold text-tp-ink ' +
  'transition-[border-color,background-color] duration-150 ease-tp-out [@media(hover:hover)]:hover:bg-tp-surface ' +
  'focus-within:border-tp-brand focus-within:ring-2 focus-within:ring-tp-brand/20';

export default function TripMeta({
  startDate,
  onChangeStartDate,
  nights,
  vehicleType,
  onChangeVehicleType,
  maxDriveHours,
  onChangeMaxDriveHours,
  compact = false,
}: {
  startDate: string | null;
  onChangeStartDate: (date: string | null) => void;
  nights: number;
  vehicleType: string;
  onChangeVehicleType: (type: string) => void;
  maxDriveHours?: number;
  /** How long a day you like to drive (splits long days into nights). */
  onChangeMaxDriveHours?: (hours: number) => void;
  /**
   * Phones (the planner sheet): one row of 36px chips, icon + value, the
   * same native pickers underneath. The "why it matters" line moves into
   * each control's accessible name and title.
   */
  compact?: boolean;
}) {
  const dateRef = useRef<HTMLInputElement>(null);
  const range = dateRangeLabel(startDate, nights);
  const openPicker = () => {
    const el = dateRef.current;
    if (!el) return;
    try {
      (el as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
    } catch {
      /* Safari < 16: fall through to focus */
    }
    el.focus();
  };
  const vehicleKnown = VEHICLE_OPTIONS.some((o) => o.value === vehicleType);
  const dateWord = dateCellLabel(startDate);
  const chevron = 'pointer-events-none -ml-0.5 h-3.5 w-3.5 flex-none text-tp-muted';

  if (compact) {
    return (
      <>
        <span className={`${CHIP} ${startDate ? 'pr-1' : 'pr-3'}`}>
          <button
            type="button"
            onClick={openPicker}
            className="inline-flex h-full min-h-0 min-w-0 items-center gap-1.5 border-0 bg-transparent p-0 text-left text-inherit [font-family:inherit] focus:outline-none"
            aria-label={range ? `Trip dates: ${range}. Change departure date` : 'Add a departure date. Puts a date on each day'}
            title={range ? 'Change the departure date' : 'Puts a date on each day'}
            data-testid="trip-meta-dates"
          >
            <IconCalendar className="h-4 w-4 flex-none text-tp-muted" />
            <span className={`whitespace-nowrap ${range ? 'tabular-nums' : ''}`}>{range || 'Add dates'}</span>
          </button>
          {startDate && (
            <button
              type="button"
              onClick={() => onChangeStartDate(null)}
              aria-label="Clear dates"
              title="Clear dates"
              className="flex h-7 w-7 min-h-0 flex-none items-center justify-center rounded-full border-0 bg-transparent text-tp-faint hover:text-tp-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
            >
              <IconX className="h-3.5 w-3.5" />
            </button>
          )}
          <input
            ref={dateRef}
            type="date"
            tabIndex={-1}
            aria-hidden="true"
            value={startDate ?? ''}
            onChange={(e) => onChangeStartDate(e.target.value || null)}
            className="pointer-events-none absolute bottom-0 left-2 h-px w-px opacity-0"
          />
        </span>
        <label className={`${CHIP} cursor-pointer pr-7`} title={VEHICLE_TITLE} data-testid="trip-meta-vehicle">
          <span className="flex-none text-tp-muted">{vehicleIcon(vehicleType)}</span>
          <span className="whitespace-nowrap">{vehicleLabel(vehicleType)}</span>
          <span className="whitespace-nowrap font-normal text-[#4a5a73]">· {vehicleEffect(vehicleType)}</span>
          <select
            value={vehicleType}
            onChange={(e) => onChangeVehicleType(e.target.value)}
            aria-label={`Vehicle: ${vehicleLabel(vehicleType)}, ${vehicleEffect(vehicleType)}. Sets drive times and which camps we suggest`}
            className="tp-meta-select absolute inset-0 h-full min-h-0 w-full cursor-pointer appearance-none border-0 bg-transparent p-0 opacity-0 [font-family:inherit] text-[16px]"
            style={{ margin: 0, width: '100%' }}
          >
            {!vehicleKnown && <option value={vehicleType}>{vehicleType}</option>}
            {VEHICLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <IconChevronDown className="pointer-events-none absolute right-2.5 h-3.5 w-3.5 text-tp-muted" />
        </label>
        {onChangeMaxDriveHours && (
          <label className={`${CHIP} cursor-pointer pr-7`} title="How long you want to drive each day">
            <IconClock className="h-4 w-4 flex-none text-tp-muted" />
            <span className="whitespace-nowrap">{`${maxDriveHours ?? 6}h a day`}</span>
            <select
              value={String(maxDriveHours ?? 6)}
              onChange={(e) => onChangeMaxDriveHours(Number(e.target.value))}
              aria-label="Driving per day. Splits long days"
              className="tp-meta-select absolute inset-0 h-full min-h-0 w-full cursor-pointer appearance-none border-0 bg-transparent p-0 opacity-0 [font-family:inherit] text-[16px]"
              style={{ margin: 0, width: '100%' }}
            >
              {[3, 4, 5, 6, 7, 8, 10].map((h) => (
                <option key={h} value={h}>
                  {h}h a day
                </option>
              ))}
            </select>
            <IconChevronDown className="pointer-events-none absolute right-2.5 h-3.5 w-3.5 text-tp-muted" />
          </label>
        )}
      </>
    );
  }

  return (
    <div
      className="grid w-full grid-cols-3 divide-x divide-y-0 divide-solid divide-tp-line overflow-visible rounded-tp border border-solid border-tp-line bg-white"
      role="group"
      aria-label="Trip settings"
      data-testid="trip-meta"
    >
      <span
        className={`${CONTROL}`}
        title={range ? `${range}. Dates on each day` : 'Puts a date on each day'}
        data-empty={startDate ? undefined : ''}
      >
        <button
          type="button"
          onClick={openPicker}
          className="absolute inset-0 z-[1] min-h-0 rounded-[inherit] border-0 bg-transparent p-0 focus:outline-none"
          aria-label={range ? `Trip dates: ${range}. Change departure date` : 'Add a departure date'}
        />
        {/* Empty, it is an offer: a brand "+" and the words in ink, no
            chevron (there is nothing to change yet). Set, it is a value. */}
        {dateWord ? (
          <IconCalendar className="h-4 w-4 flex-none text-tp-muted" />
        ) : (
          <IconPlus className="h-4 w-4 flex-none text-tp-brand" />
        )}
        <Label text={dateWord || 'Add dates'} numeric={!!dateWord} />
        {dateWord ? <IconChevronDown className={chevron} /> : null}
        {/* The real picker: invisible, under the trigger, opened by showPicker(). */}
        <input
          ref={dateRef}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          value={startDate ?? ''}
          onChange={(e) => onChangeStartDate(e.target.value || null)}
          className="pointer-events-none absolute bottom-0 left-2 h-px w-px opacity-0"
        />
      </span>

      <label className={`${CONTROL} cursor-pointer`} title="Your vehicle sets which roads we route on and which fuel stops we show">
        <span className="flex-none text-tp-muted">{vehicleIcon(vehicleType)}</span>
        <Label text={vehicleLabel(vehicleType)} />
        <select
          value={vehicleType}
          onChange={(e) => onChangeVehicleType(e.target.value)}
          aria-label="Vehicle. Sets road and fuel rules"
          className="tp-meta-select absolute inset-0 h-full min-h-0 w-full cursor-pointer appearance-none border-0 bg-transparent p-0 opacity-0 [font-family:inherit] [@media(pointer:coarse)]:text-[16px]"
          style={{ margin: 0, width: '100%' }}
        >
          {!vehicleKnown && <option value={vehicleType}>{vehicleType}</option>}
          {VEHICLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <IconChevronDown className={chevron} />
      </label>

      {onChangeMaxDriveHours && (
        <label className={`${CONTROL} cursor-pointer`} title="How long you like to drive each day. Splits long days.">
          <IconClock className="h-4 w-4 flex-none text-tp-muted" />
          <Label text={`${maxDriveHours ?? 6}h a day`} />
          <select
            value={String(maxDriveHours ?? 6)}
            onChange={(e) => onChangeMaxDriveHours(Number(e.target.value))}
            aria-label="Driving per day. Splits long days"
            className="tp-meta-select absolute inset-0 h-full min-h-0 w-full cursor-pointer appearance-none border-0 bg-transparent p-0 opacity-0 [font-family:inherit] [@media(pointer:coarse)]:text-[16px]"
            style={{ margin: 0, width: '100%' }}
          >
            {[3, 4, 5, 6, 7, 8, 10].map((h) => (
              <option key={h} value={h}>
                {h}h a day
              </option>
            ))}
          </select>
          <IconChevronDown className={chevron} />
        </label>
      )}
    </div>
  );
}

function vehicleLabel(type: string): string {
  return VEHICLE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

/** The control's word: one line, the same size in every pill. Muted when it is a prompt, not a value. */
function Label({ text, numeric = false, muted = false }: { text: string; numeric?: boolean; muted?: boolean }) {
  return (
    <span
      className={`min-w-0 truncate text-left text-[13px] max-[400px]:text-[12.5px] max-[400px]:tracking-[-0.01em] ${muted ? 'font-medium text-tp-muted' : 'font-semibold text-tp-ink'} ${numeric ? 'tabular-nums' : ''}`}
      aria-hidden="true"
    >
      {text}
    </span>
  );
}

/** "Thu 12 Nov · Caravan · 6h a day" — the phone's folded row, as words. Pure. */
export function tripMetaSummaryParts(
  startDate: string | null | undefined,
  nights: number,
  vehicleType: string,
  maxDriveHours?: number
): { key: 'dates' | 'vehicle' | 'pace'; text: string; empty?: boolean }[] {
  const range = dateRangeLabel(startDate, nights);
  return [
    { key: 'dates', text: range || 'Add dates', empty: !range },
    { key: 'vehicle', text: vehicleLabel(vehicleType) },
    { key: 'pace', text: `${maxDriveHours ?? 6}h a day` },
  ];
}

/**
 * Phones: the three controls folded into one line, the same icons as the
 * pills. An empty date reads as a prompt ("+ Add dates", muted).
 */
export function TripMetaSummary({
  startDate,
  nights,
  vehicleType,
  maxDriveHours,
}: {
  startDate: string | null;
  nights: number;
  vehicleType: string;
  maxDriveHours?: number;
}) {
  const parts = tripMetaSummaryParts(startDate, nights, vehicleType, maxDriveHours);
  return (
    <span className="flex min-w-0 items-center gap-2 whitespace-nowrap">
      {parts.map((part, i) => (
        <React.Fragment key={part.key}>
          {i > 0 && (
            <span className="text-tp-line-strong" aria-hidden="true">
              ·
            </span>
          )}
          <span className={`inline-flex min-w-0 items-center gap-1.5 ${part.empty ? 'text-tp-muted' : 'font-semibold text-tp-ink'}`}>
            <span className="flex-none text-tp-muted">
              {part.key === 'dates' ? (
                part.empty ? (
                  <IconPlus className="h-4 w-4" />
                ) : (
                  <IconCalendar className="h-4 w-4" />
                )
              ) : part.key === 'vehicle' ? (
                vehicleIcon(vehicleType)
              ) : (
                <IconClock className="h-4 w-4" />
              )}
            </span>
            <span className={part.key === 'dates' ? 'tabular-nums' : ''}>{part.text}</span>
          </span>
        </React.Fragment>
      ))}
    </span>
  );
}
